import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { SelectInput } from '@/components/SelectInput';
import { useAuthStore } from '@/stores/useAuthStore';
import { useCardStore } from '@/stores/useCardStore';
import { Card } from '@/types/card';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface LinkAccessoryToCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLinkToCard: (cardId: number) => void;
}

export const LinkAccessoryToCardModal = ({
  isOpen,
  onClose,
  onLinkToCard,
}: LinkAccessoryToCardModalProps) => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { listCards, cards, loading } = useCardStore();
  const [selectedCard, setSelectedCard] = useState<number | null>(null);

  // Réinitialiser les entrées lorsque le modal se ferme
  useEffect(() => {
    if (!isOpen) {
      setSelectedCard(null);
    }
  }, [isOpen]);

  // Charger les cartes lorsque le modal s'ouvre
  useEffect(() => {
    if (isOpen) {
      const fetchCards = async () => {
        if (user?.role === 'user') {
          await listCards({
            user_id: user.id,
            paginate: 0,
          });
        } else if (user?.role === 'manager' && user.company?.id) {
          await listCards({
            company_id: user.company.id,
            paginate: 0,
          });
        } else if (user?.role === 'admin') {
          await listCards({ paginate: 0 });
        }
      };

      fetchCards();
    }
  }, [isOpen, listCards, user?.id, user?.role, user?.company?.id]);

  const handleCardSelect = (e: {
    target: {
      name: string;
      value: string | number | boolean | (string | number | boolean)[];
    };
  }) => {
    setSelectedCard(Number(e.target.value));
  };

  const handleClose = () => {
    setSelectedCard(null);
    onClose();
  };

  const handleLinkToCard = () => {
    if (selectedCard) {
      onLinkToCard(selectedCard);
      handleClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('linkAccessoryModal:title', "Lier l'accessoire à une carte")}
      className="w-full max-w-md"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          {t(
            'linkAccessoryModal:description',
            'Sélectionnez une carte existante à laquelle lier cet accessoire.',
          )}
        </p>

        <SelectInput
          label={t('linkAccessoryModal:selectCard', 'Sélectionner une carte')}
          options={cards.map((card: Card) => ({
            value: card.id.toString(),
            label: card.name,
          }))}
          name="card"
          defaultValue={selectedCard?.toString() || ''}
          onChange={handleCardSelect}
          useFormik={false}
          isLoading={loading}
        />

        <div className="mt-4 flex justify-end gap-2">
          <Button onClick={handleClose}>{t('common:cancel', 'Annuler')}</Button>
          <Button
            onClick={handleLinkToCard}
            disabled={!selectedCard}
            className="bg-linko-dark text-white"
          >
            {t('linkAccessoryModal:link', 'Lier')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
